import React, { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Mail, ShieldAlert, Power, Clock, CheckCircle2, XCircle, BanIcon, AlertTriangle, RefreshCw } from "lucide-react";

const STATUS_LABELS = {
  sent: { label: "Enviado", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  blocked: { label: "Bloqueado", color: "bg-amber-100 text-amber-700", icon: BanIcon },
  error: { label: "Error", color: "bg-red-100 text-red-700", icon: AlertTriangle },
};

const TRIGGER_LABEL = {
  manual: { label: "Manual", color: "bg-sky-100 text-sky-700" },
  automatico: { label: "Automático", color: "bg-violet-100 text-violet-700" },
};

export default function AdminCorreos() {
  const [data, setData] = useState({ master_enabled: false, items: [] });
  const [loading, setLoading] = useState(true);
  const [log, setLog] = useState({ items: [], summary: { sent: 0, blocked: 0, error: 0 }, since: null });
  const [logLoading, setLogLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-settings");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);

  const cargarLog = useCallback(async () => {
    setLogLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("days", "7");
      params.set("limit", "150");
      if (filterStatus) params.set("status", filterStatus);
      const { data } = await api.get(`/admin/email-settings/log?${params}`);
      setLog(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLogLoading(false); }
  }, [filterStatus]);

  useEffect(() => { cargar(); cargarLog(); }, [cargar, cargarLog]);

  const toggle = async (key, enabled) => {
    try {
      await api.patch(`/admin/email-settings/${key}`, { enabled });
      toast.success(enabled ? "Activado" : "Desactivado");
      cargar();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Agrupar items por categoría
  const grouped = (data.items || []).reduce((acc, it) => {
    const cat = it.categoria || "Otros";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(it);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="admin-correos-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#15241d] flex items-center gap-2">
            <Mail size={22} className="text-[#205c45]" />
            Envíos automáticos de correo
          </h1>
          <p className="text-sm text-[#5c7368] mt-0.5">
            Controla qué correos automáticos envía el sistema. Por defecto todos están desactivados.
          </p>
        </div>
      </div>

      {/* Master switch */}
      <Card className="border-2 border-[#205c45] p-4 bg-gradient-to-r from-[#f7fbf9] to-white">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-full ${data.master_enabled ? "bg-emerald-100" : "bg-red-100"}`}>
            <Power size={24} className={data.master_enabled ? "text-emerald-600" : "text-red-600"} />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-[#15241d]">Envío global de correos</div>
            <div className="text-xs text-[#5c7368]">
              Cuando está apagado, ningún correo del sistema se envía (manual ni automático), aunque los tipos individuales estén activos.
            </div>
          </div>
          <Switch
            checked={data.master_enabled}
            onCheckedChange={(v) => toggle("global", v)}
            disabled={loading}
            data-testid="master-switch"
          />
        </div>
        {!data.master_enabled && (
          <div className="mt-3 p-2.5 rounded bg-amber-50 border border-amber-200 text-[12.5px] text-amber-900 flex items-start gap-2">
            <ShieldAlert size={14} className="mt-0.5 shrink-0" />
            <span>
              <b>Modo silencioso activo.</b> Ningún correo se envía actualmente. Activa el switch global para reanudar el sistema.
            </span>
          </div>
        )}
      </Card>

      <Tabs defaultValue="config">
        <TabsList className="bg-white border border-[#d1dbd6]">
          <TabsTrigger value="config" data-testid="tab-config">Configuración por tipo</TabsTrigger>
          <TabsTrigger value="log" data-testid="tab-log">Historial de envíos</TabsTrigger>
        </TabsList>

        <TabsContent value="config" className="mt-4 space-y-4">
          {Object.entries(grouped).map(([cat, items]) => (
            <Card key={cat} className="p-4">
              <div className="font-semibold text-[#205c45] mb-3 text-sm uppercase tracking-wide">{cat}</div>
              <div className="space-y-3">
                {items.map((it) => {
                  const trig = TRIGGER_LABEL[it.trigger] || TRIGGER_LABEL.manual;
                  return (
                    <div key={it.key} className="flex items-start gap-3 p-3 border border-[#e6ecea] rounded-lg hover:bg-[#f7fbf9]">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-medium text-[#15241d]">{it.label}</div>
                          <Badge variant="outline" className={`text-[10px] ${trig.color} border-0`}>
                            {it.trigger === "automatico" ? <Clock size={10} className="mr-1 inline" /> : null}
                            {trig.label}
                          </Badge>
                        </div>
                        <div className="text-[12.5px] text-[#5c7368] mt-0.5">{it.descripcion}</div>
                        {it.updated_by && (
                          <div className="text-[10.5px] text-[#8ba397] mt-1">
                            Última modificación: {(it.updated_at || "").slice(0, 16).replace("T", " ")} por {it.updated_by}
                          </div>
                        )}
                      </div>
                      <Switch
                        checked={it.enabled}
                        onCheckedChange={(v) => toggle(it.key, v)}
                        data-testid={`toggle-${it.key}`}
                      />
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="log" className="mt-4">
          <Card className="p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex gap-2 items-center text-sm">
                <span className="text-[#5c7368]">Resumen (últimos 7 días):</span>
                <Badge className="bg-emerald-100 text-emerald-700">
                  <CheckCircle2 size={11} className="mr-1" /> {log.summary.sent} enviados
                </Badge>
                <Badge className="bg-amber-100 text-amber-700">
                  <BanIcon size={11} className="mr-1" /> {log.summary.blocked} bloqueados
                </Badge>
                <Badge className="bg-red-100 text-red-700">
                  <AlertTriangle size={11} className="mr-1" /> {log.summary.error} errores
                </Badge>
              </div>
              <div className="flex gap-2 items-center">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="text-xs px-2 py-1 border border-[#d1dbd6] rounded h-8"
                  data-testid="filter-status"
                >
                  <option value="">Todos los estados</option>
                  <option value="sent">Enviados</option>
                  <option value="blocked">Bloqueados</option>
                  <option value="error">Errores</option>
                </select>
                <Button size="sm" variant="outline" onClick={cargarLog} disabled={logLoading} data-testid="reload-log-btn">
                  <RefreshCw size={12} className={`mr-1 ${logLoading ? "animate-spin" : ""}`} />
                  Recargar
                </Button>
              </div>
            </div>

            <div className="border border-[#e6ecea] rounded overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-[#f7fbf9]">
                  <tr className="text-left text-[#5c7368]">
                    <th className="p-2">Fecha/Hora</th>
                    <th className="p-2">Tipo</th>
                    <th className="p-2">Destinatario(s)</th>
                    <th className="p-2">Asunto</th>
                    <th className="p-2">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {log.items.length === 0 && (
                    <tr><td colSpan={5} className="text-center py-6 text-[#8ba397]">Sin registros</td></tr>
                  )}
                  {log.items.map((r, i) => {
                    const st = STATUS_LABELS[r.status] || STATUS_LABELS.error;
                    const Ico = st.icon;
                    return (
                      <tr key={i} className="border-t border-[#e6ecea] hover:bg-[#f7fbf9]">
                        <td className="p-2 text-[11px] text-[#5c7368] whitespace-nowrap">
                          {(r.ts || "").slice(0, 19).replace("T", " ")}
                        </td>
                        <td className="p-2 text-[11px] font-mono text-[#15241d]">{r.email_type}</td>
                        <td className="p-2 text-[11px] truncate max-w-[200px]" title={(r.to || []).join(", ")}>
                          {(r.to || []).join(", ") || "—"}
                        </td>
                        <td className="p-2 text-[11px] truncate max-w-[260px]" title={r.subject}>
                          {r.subject || "—"}
                        </td>
                        <td className="p-2 whitespace-nowrap">
                          <Badge className={`${st.color} text-[10px]`}>
                            <Ico size={10} className="mr-1" /> {st.label}
                          </Badge>
                          {r.reason && r.reason !== "ok" && (
                            <div className="text-[10px] text-[#8ba397] mt-0.5" title={r.error || ""}>
                              {r.reason}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
